/**
 * Canvas-based Live Mockup compositor (iter114k → iter114l).
 *
 * Replaces the Gemini Nano Banana Universal-Key-billed render with a
 * 100%-free, browser-side HTML5 Canvas composite. Renders the customer's
 * actual uploaded artwork as a "sticker pack" on a rotating library of
 * EG3-branded surfaces, with the chosen cut shape applied pixel-perfect
 * AND a material-aware sticker backing (holographic rainbow, glitter
 * speckle, clear/transparent, gold/silver foil, etc.) so the customer
 * sees the actual product they selected — not just a white sticker.
 *
 * Public API:
 *   await renderCanvasMockup({ artworkBlob, cutShape, material, sceneSeed })
 *     → string (PNG data URL)
 *
 * Why this is better than the AI path it replaces:
 *   - 100% free (no Universal Key spend, no 402 errors).
 *   - ~50 ms render vs 10–20 s Gemini wait.
 *   - Shows the CUSTOMER'S actual artwork, not an AI hallucination.
 *   - Cut shape applies EXACTLY (matches what they'll be shipped).
 *   - MATERIAL-AWARE: holographic stickers actually look holographic;
 *     glitter looks sparkly; clear shows the surface through the sticker;
 *     foils shimmer. (iter114l)
 *   - ROTATING SCENES: 6 different surfaces rotate per artwork upload
 *     so the customer never gets bored looking at the same emerald
 *     velvet panel. (iter114l)
 */

const CANVAS_W = 1600;
const CANVAS_H = 1000;

// Sticker layout — five copies arranged so the eye lands on the largest
// front-and-centre piece first. Order matters: later entries paint ON
// TOP, so the front-and-centre largest sticker is last.
const STICKER_LAYOUT = [
  { cx: 1280, cy: 280, size: 280, rot: 8 },   // back-right
  { cx: 320,  cy: 320, size: 290, rot: -10 }, // back-left
  { cx: 1180, cy: 720, size: 320, rot: -6 },  // mid-right
  { cx: 380,  cy: 740, size: 310, rot: 7 },   // mid-left
  { cx: 800,  cy: 500, size: 460, rot: -3 },  // hero centre
];

// ---------------------------------------------------------------------
// SCENE LIBRARY (iter114l → iter114m)
// ---------------------------------------------------------------------
// Each scene is a paint function that fills the entire canvas. Scenes
// are picked deterministically via `pickSceneIndex(seed)` so a given
// artwork always lands on the same surface across cut-shape tweaks
// (no flickering between renders), but a different artwork upload —
// or a manual "shuffle" — gets a different surface.
//
// iter114m — DROPPED the EG3-branded `emerald-velvet` scene per
// customer feedback ("don't like the preview-eg3 background"). The
// remaining 8 scenes are all neutral/lifestyle surfaces with subtle
// grain — none of them carry EG3 brand colours. Watermark density was
// also reduced (single corner stamp instead of repeating diagonals).
const SCENES = [
  { id: 'walnut-desk',     paint: drawWalnutDesk,     watermarkColor: 'rgba(255, 255, 255, 0.32)' },
  { id: 'white-marble',    paint: drawWhiteMarble,    watermarkColor: 'rgba(40, 40, 40, 0.28)' },
  { id: 'concrete-studio', paint: drawConcreteStudio, watermarkColor: 'rgba(40, 40, 40, 0.30)' },
  { id: 'cork-board',      paint: drawCorkBoard,      watermarkColor: 'rgba(255, 255, 255, 0.30)' },
  { id: 'denim',           paint: drawDenim,          watermarkColor: 'rgba(255, 255, 255, 0.32)' },
  { id: 'kraft-paper',     paint: drawKraftPaper,     watermarkColor: 'rgba(40, 30, 15, 0.28)' },
  { id: 'brushed-steel',   paint: drawBrushedSteel,   watermarkColor: 'rgba(40, 40, 40, 0.28)' },
  { id: 'tan-leather',     paint: drawTanLeather,     watermarkColor: 'rgba(255, 240, 220, 0.30)' },
];

/**
 * Tiny string hash → integer in [0, n). djb2 — good enough distribution
 * for picking 1 of N scenes, and stable across browsers and sessions.
 */
const hashToIndex = (input, n) => {
  const s = String(input || '');
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) + h + s.charCodeAt(i)) & 0xffffffff; // djb2 mix
  }
  return Math.abs(h) % Math.max(1, n);
};

const pickScene = (seed) => SCENES[hashToIndex(seed, SCENES.length)];

// ---------------------------------------------------------------------
// SCENE PAINTERS
// ---------------------------------------------------------------------

function drawWalnutDesk(ctx) {
  // Warm walnut wood-grain — diagonal stripes of varying browns.
  const base = ctx.createLinearGradient(0, 0, CANVAS_W, CANVAS_H);
  base.addColorStop(0, '#5b3a1c');
  base.addColorStop(0.5, '#7a4f28');
  base.addColorStop(1, '#3e2812');
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  // Wood grain — thin darker lines roughly horizontal.
  ctx.save();
  ctx.globalAlpha = 0.28;
  ctx.strokeStyle = '#2b1809';
  ctx.lineWidth = 1.5;
  for (let y = 0; y < CANVAS_H; y += 6 + (y % 13)) {
    ctx.beginPath();
    ctx.moveTo(0, y + Math.sin(y * 0.04) * 6);
    ctx.bezierCurveTo(
      CANVAS_W * 0.33, y + Math.sin(y * 0.07) * 12,
      CANVAS_W * 0.66, y + Math.sin(y * 0.05) * 14,
      CANVAS_W, y + Math.sin(y * 0.06) * 9,
    );
    ctx.stroke();
  }
  ctx.restore();
  // Soft top-light vignette.
  const v = ctx.createRadialGradient(CANVAS_W / 2, CANVAS_H * 0.15, 80, CANVAS_W / 2, CANVAS_H * 0.5, 1100);
  v.addColorStop(0, 'rgba(255, 230, 180, 0.18)');
  v.addColorStop(1, 'rgba(0, 0, 0, 0.35)');
  ctx.fillStyle = v;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
}

function drawWhiteMarble(ctx) {
  // Cool off-white with subtle grey veins.
  ctx.fillStyle = '#f4f2ee';
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  ctx.save();
  ctx.globalAlpha = 0.18;
  ctx.strokeStyle = '#9a9a9a';
  ctx.lineWidth = 1.2;
  for (let i = 0; i < 24; i += 1) {
    ctx.beginPath();
    const x0 = Math.random() * CANVAS_W;
    const y0 = Math.random() * CANVAS_H;
    ctx.moveTo(x0, y0);
    ctx.bezierCurveTo(
      x0 + 200, y0 + 60,
      x0 + 380, y0 + 180,
      x0 + 600, y0 + 240,
    );
    ctx.stroke();
  }
  ctx.restore();
  // Soft cool vignette.
  const v = ctx.createRadialGradient(CANVAS_W / 2, CANVAS_H / 2, 200, CANVAS_W / 2, CANVAS_H / 2, 1100);
  v.addColorStop(0, 'rgba(0, 0, 0, 0)');
  v.addColorStop(1, 'rgba(40, 50, 60, 0.18)');
  ctx.fillStyle = v;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
}

function drawConcreteStudio(ctx) {
  // Mid-grey concrete — flat base + grain noise.
  ctx.fillStyle = '#9a9a96';
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  ctx.save();
  for (let i = 0; i < 5000; i += 1) {
    const x = Math.random() * CANVAS_W;
    const y = Math.random() * CANVAS_H;
    const a = 0.05 + Math.random() * 0.12;
    ctx.fillStyle = Math.random() > 0.5
      ? `rgba(60, 60, 60, ${a})`
      : `rgba(220, 220, 220, ${a})`;
    ctx.fillRect(x, y, 2, 2);
  }
  ctx.restore();
  // Soft directional light from top-left.
  const v = ctx.createRadialGradient(CANVAS_W * 0.3, CANVAS_H * 0.2, 60, CANVAS_W * 0.5, CANVAS_H * 0.5, 1200);
  v.addColorStop(0, 'rgba(255, 255, 255, 0.20)');
  v.addColorStop(1, 'rgba(0, 0, 0, 0.30)');
  ctx.fillStyle = v;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
}

function drawCorkBoard(ctx) {
  // Tan cork base + speckle.
  ctx.fillStyle = '#b8965b';
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  ctx.save();
  for (let i = 0; i < 3500; i += 1) {
    const x = Math.random() * CANVAS_W;
    const y = Math.random() * CANVAS_H;
    const r = 1 + Math.random() * 3;
    const dark = Math.random() > 0.5;
    ctx.fillStyle = dark
      ? `rgba(80, 50, 20, ${0.18 + Math.random() * 0.25})`
      : `rgba(230, 200, 150, ${0.18 + Math.random() * 0.20})`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
  const v = ctx.createRadialGradient(CANVAS_W / 2, CANVAS_H / 2, 200, CANVAS_W / 2, CANVAS_H / 2, 1100);
  v.addColorStop(0, 'rgba(0, 0, 0, 0)');
  v.addColorStop(1, 'rgba(40, 25, 10, 0.30)');
  ctx.fillStyle = v;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
}

function drawDenim(ctx) {
  // Indigo blue base with crosshatch weave hint.
  ctx.fillStyle = '#2d4a78';
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  ctx.save();
  ctx.globalAlpha = 0.22;
  ctx.strokeStyle = '#1a2c4a';
  ctx.lineWidth = 1;
  for (let y = 0; y < CANVAS_H; y += 4) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(CANVAS_W, y);
    ctx.stroke();
  }
  ctx.strokeStyle = '#3f608f';
  for (let x = 0; x < CANVAS_W; x += 4) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, CANVAS_H);
    ctx.stroke();
  }
  ctx.restore();
  const v = ctx.createRadialGradient(CANVAS_W / 2, CANVAS_H / 2, 200, CANVAS_W / 2, CANVAS_H / 2, 1100);
  v.addColorStop(0, 'rgba(255, 255, 255, 0.10)');
  v.addColorStop(1, 'rgba(0, 0, 30, 0.45)');
  ctx.fillStyle = v;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
}

function drawKraftPaper(ctx) {
  // Warm brown kraft paper — flat base with fibre noise.
  ctx.fillStyle = '#b89568';
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  ctx.save();
  // Fibre noise — short dark/light streaks scattered across the page.
  for (let i = 0; i < 6000; i += 1) {
    const x = Math.random() * CANVAS_W;
    const y = Math.random() * CANVAS_H;
    const dark = Math.random() > 0.55;
    ctx.fillStyle = dark
      ? `rgba(80, 55, 25, ${0.10 + Math.random() * 0.16})`
      : `rgba(245, 220, 180, ${0.10 + Math.random() * 0.18})`;
    ctx.fillRect(x, y, 2 + Math.random() * 3, 1);
  }
  ctx.restore();
  // Soft warm vignette.
  const v = ctx.createRadialGradient(
    CANVAS_W * 0.45, CANVAS_H * 0.4, 200,
    CANVAS_W / 2, CANVAS_H / 2, 1100,
  );
  v.addColorStop(0, 'rgba(255, 230, 180, 0.10)');
  v.addColorStop(1, 'rgba(60, 35, 10, 0.30)');
  ctx.fillStyle = v;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
}

function drawBrushedSteel(ctx) {
  // Cool grey gradient + horizontal brushed lines for a metallic feel.
  const base = ctx.createLinearGradient(0, 0, 0, CANVAS_H);
  base.addColorStop(0, '#bdbdbd');
  base.addColorStop(0.5, '#d4d4d4');
  base.addColorStop(1, '#9e9e9e');
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  ctx.save();
  // Brushed horizontal lines — many low-contrast strokes.
  for (let i = 0; i < 800; i += 1) {
    const y = Math.random() * CANVAS_H;
    const w = 200 + Math.random() * 1200;
    const x = Math.random() * (CANVAS_W - w);
    const a = 0.05 + Math.random() * 0.10;
    ctx.strokeStyle = Math.random() > 0.5
      ? `rgba(80, 80, 80, ${a})`
      : `rgba(245, 245, 245, ${a})`;
    ctx.lineWidth = 0.6;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + w, y + (Math.random() - 0.5) * 1.5);
    ctx.stroke();
  }
  ctx.restore();
  // Top-left ambient highlight.
  const v = ctx.createRadialGradient(
    CANVAS_W * 0.3, CANVAS_H * 0.2, 60,
    CANVAS_W / 2, CANVAS_H / 2, 1200,
  );
  v.addColorStop(0, 'rgba(255, 255, 255, 0.20)');
  v.addColorStop(1, 'rgba(20, 20, 20, 0.18)');
  ctx.fillStyle = v;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
}

function drawTanLeather(ctx) {
  // Saddle brown base + leather-grain dimples.
  const base = ctx.createRadialGradient(
    CANVAS_W / 2, CANVAS_H / 2, 200,
    CANVAS_W / 2, CANVAS_H / 2, 1100,
  );
  base.addColorStop(0, '#9a6536');
  base.addColorStop(1, '#5b3a1c');
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  ctx.save();
  // Leather grain — small overlapping circles for the dimpled hide look.
  for (let i = 0; i < 2200; i += 1) {
    const x = Math.random() * CANVAS_W;
    const y = Math.random() * CANVAS_H;
    const r = 3 + Math.random() * 9;
    const dark = Math.random() > 0.5;
    ctx.fillStyle = dark
      ? `rgba(50, 30, 10, ${0.10 + Math.random() * 0.14})`
      : `rgba(220, 180, 130, ${0.05 + Math.random() * 0.10})`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
  // Warm vignette.
  const v = ctx.createRadialGradient(CANVAS_W / 2, CANVAS_H / 2, 200, CANVAS_W / 2, CANVAS_H / 2, 1100);
  v.addColorStop(0, 'rgba(255, 200, 130, 0.08)');
  v.addColorStop(1, 'rgba(20, 10, 0, 0.40)');
  ctx.fillStyle = v;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
}

// ---------------------------------------------------------------------
// MATERIAL-AWARE STICKER BACKING (iter114l)
// ---------------------------------------------------------------------
// Different stock materials get different "white border" treatments so
// the customer sees the ACTUAL product they ordered (a holographic
// sticker has a rainbow border, not a plain white one). Detection is
// substring-based on the material name passed in from the parent — see
// `frontend/src/data/mockData.js` for the canonical material names.

// ---------------------------------------------------------------------
// MATERIAL TEXTURE LOADER (iter114s.3)
// ---------------------------------------------------------------------
// The backend's proof PDF generator uses the SAME glitter / hologram
// JPG textures from `/app/backend/static/`. We mirror those into
// `frontend/public/material-textures/` so the live mockup pulls from
// THE SAME source-of-truth texture and the customer sees an identical
// finish on the canvas preview, the proof PDF, and the printed
// product. Lazy-loaded once per page session and cached as a Promise
// so concurrent renders don't trigger duplicate fetches.
const MATERIAL_TEXTURE_URLS = {
  glitter: '/material-textures/glitter_background.jpg',
  holographic: '/material-textures/hologram_background.jpg',
};
const _textureCache = {};
const loadMaterialTexture = (key) => {
  if (_textureCache[key]) return _textureCache[key];
  const url = MATERIAL_TEXTURE_URLS[key];
  if (!url) return Promise.resolve(null);
  _textureCache[key] = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null); // graceful fallback to procedural
    img.src = url;
  });
  return _textureCache[key];
};

const lc = (s) => String(s || '').toLowerCase();

const isHolographic = (m) => lc(m).includes('holograph');
const isGlitter     = (m) => lc(m).includes('glitter');
const isClear       = (m) => /clear|transparent|crystal/.test(lc(m));
const isGoldFoil    = (m) => /gold|brass/.test(lc(m)) && /foil|stamp/.test(lc(m));
const isSilverFoil  = (m) => /silver|chrome/.test(lc(m)) && /foil|stamp/.test(lc(m));
const isAnyFoil     = (m) => /foil|stamp/.test(lc(m));
const isBlackVinyl  = (m) => lc(m).includes('black') && lc(m).includes('vinyl');

/**
 * Paint the full die-cut border + artwork backing in a single pass.
 * Caller has already translated/rotated to the sticker's centre and
 * clipped to the cut shape at full (border-included) dimensions.
 *
 * iter114s.3 — `textures` is an optional object of pre-loaded
 * HTMLImageElements keyed by material name (`glitter`, `holographic`).
 * When present, we draw the actual texture image (matching the backend
 * proof PDF) instead of the procedural fallback. When the texture
 * failed to load (slow network / cache miss) we fall through to the
 * procedural fallback so the mockup still renders.
 *
 * Holographic / glitter / foil materials need EXTRA decoration on top
 * of the base fill, so this function does both the fill AND the
 * decoration in one call.
 */
function paintMaterialBacking(ctx, material, fullW, fullH, textures = {}) {
  // Holographic — diagonal rainbow gradient. Looks shimmery in motion
  // when the cut-shape clip is in place.
  if (isHolographic(material)) {
    const grad = ctx.createLinearGradient(-fullW / 2, -fullH / 2, fullW / 2, fullH / 2);
    grad.addColorStop(0,    '#ff9ad9');
    grad.addColorStop(0.18, '#a78bfa');
    grad.addColorStop(0.36, '#67e8f9');
    grad.addColorStop(0.54, '#86efac');
    grad.addColorStop(0.72, '#fde68a');
    grad.addColorStop(0.90, '#fb923c');
    grad.addColorStop(1,    '#ec4899');
    ctx.fillStyle = grad;
    ctx.fillRect(-fullW / 2, -fullH / 2, fullW, fullH);
    // Crossing iridescent band — gives the "shifts as you tilt" feel.
    const band = ctx.createLinearGradient(-fullW / 2, fullH / 2, fullW / 2, -fullH / 2);
    band.addColorStop(0,   'rgba(255, 255, 255, 0)');
    band.addColorStop(0.4, 'rgba(255, 255, 255, 0.55)');
    band.addColorStop(0.5, 'rgba(255, 255, 255, 0.65)');
    band.addColorStop(0.6, 'rgba(255, 255, 255, 0.45)');
    band.addColorStop(1,   'rgba(255, 255, 255, 0)');
    ctx.fillStyle = band;
    ctx.fillRect(-fullW / 2, -fullH / 2, fullW, fullH);
    return;
  }

  // Gold foil — warm metallic gradient with a brushed band.
  if (isGoldFoil(material)) {
    const grad = ctx.createLinearGradient(-fullW / 2, -fullH / 2, fullW / 2, fullH / 2);
    grad.addColorStop(0,    '#b8860b');
    grad.addColorStop(0.35, '#fde047');
    grad.addColorStop(0.5,  '#fef3c7');
    grad.addColorStop(0.65, '#fbbf24');
    grad.addColorStop(1,    '#92400e');
    ctx.fillStyle = grad;
    ctx.fillRect(-fullW / 2, -fullH / 2, fullW, fullH);
    return;
  }

  // Silver / chrome foil — cool metallic.
  if (isSilverFoil(material)) {
    const grad = ctx.createLinearGradient(-fullW / 2, -fullH / 2, fullW / 2, fullH / 2);
    grad.addColorStop(0,    '#94a3b8');
    grad.addColorStop(0.4,  '#f1f5f9');
    grad.addColorStop(0.5,  '#ffffff');
    grad.addColorStop(0.6,  '#e2e8f0');
    grad.addColorStop(1,    '#475569');
    ctx.fillStyle = grad;
    ctx.fillRect(-fullW / 2, -fullH / 2, fullW, fullH);
    return;
  }

  // Generic foil (colored foil) — warm copper-rose.
  if (isAnyFoil(material)) {
    const grad = ctx.createLinearGradient(-fullW / 2, -fullH / 2, fullW / 2, fullH / 2);
    grad.addColorStop(0,    '#b45309');
    grad.addColorStop(0.5,  '#fcd34d');
    grad.addColorStop(1,    '#9a3412');
    ctx.fillStyle = grad;
    ctx.fillRect(-fullW / 2, -fullH / 2, fullW, fullH);
    return;
  }

  // Clear / transparent — very subtle frosted tint, lets the scene
  // show through. We paint a ghost-white at low opacity so the
  // sticker silhouette is still readable.
  if (isClear(material)) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.30)';
    ctx.fillRect(-fullW / 2, -fullH / 2, fullW, fullH);
    // Add a subtle blue-grey edge to suggest "this is plastic, not paint".
    ctx.save();
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'rgba(180, 200, 220, 0.45)';
    ctx.strokeRect(-fullW / 2 + 1, -fullH / 2 + 1, fullW - 2, fullH - 2);
    ctx.restore();
    return;
  }

  // Glitter — paint the REAL backend glitter texture (`glitter_background.jpg`
  // from `/app/backend/static/`) so the live mockup matches the proof PDF
  // pixel-for-pixel. Texture is centred and "cover"-fitted to the slot
  // dimensions so it fills regardless of aspect. iter114s.3 follow-up to
  // the customer's feedback: "the glitter background used on the mock-up
  // renders is way off, it should be pulling from the same glitter
  // background as the automated pdf proof generator". When the texture
  // hasn't loaded yet (first paint, cache miss) we fall through to the
  // procedural pearlescent shimmer + sparkle bed so the mockup never
  // renders blank.
  if (isGlitter(material)) {
    const tex = textures.glitter;
    if (tex && tex.width > 0 && tex.height > 0) {
      // "Cover" fit — scale so the texture fills the entire slot in
      // both dimensions, cropping the longer side. Matches the backend
      // proof generator's BG_SCALE='cover' behaviour.
      const txRatio = tex.width / tex.height;
      const slotRatio = fullW / fullH;
      let drawW;
      let drawH;
      if (txRatio > slotRatio) {
        drawH = fullH;
        drawW = drawH * txRatio;
      } else {
        drawW = fullW;
        drawH = drawW / txRatio;
      }
      ctx.drawImage(tex, -drawW / 2, -drawH / 2, drawW, drawH);
      return;
    }
    // Fallback: pearlescent shimmer + dense sparkle bed (iter114s.2).
    const grad = ctx.createLinearGradient(-fullW / 2, -fullH / 2, fullW / 2, fullH / 2);
    grad.addColorStop(0,    '#fdf2ff');
    grad.addColorStop(0.25, '#e0e7ff');
    grad.addColorStop(0.5,  '#fef3c7');
    grad.addColorStop(0.75, '#fce7f3');
    grad.addColorStop(1,    '#dbeafe');
    ctx.fillStyle = grad;
    ctx.fillRect(-fullW / 2, -fullH / 2, fullW, fullH);
    ctx.save();
    const SPARK_COUNT = Math.round((fullW * fullH) / 400);
    const palette = ['#fde68a', '#fbcfe8', '#bae6fd', '#bbf7d0', '#fed7aa', '#ffffff', '#fef3c7', '#ddd6fe', '#fcd34d'];
    for (let i = 0; i < SPARK_COUNT; i += 1) {
      const x = -fullW / 2 + Math.random() * fullW;
      const y = -fullH / 2 + Math.random() * fullH;
      const r = 1.5 + Math.random() * 3.5;
      ctx.fillStyle = palette[(Math.random() * palette.length) | 0];
      ctx.globalAlpha = 0.7 + Math.random() * 0.3;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    const HERO_COUNT = Math.max(3, Math.round((fullW * fullH) / 6000));
    ctx.fillStyle = '#ffffff';
    for (let i = 0; i < HERO_COUNT; i += 1) {
      const x = -fullW / 2 + Math.random() * fullW;
      const y = -fullH / 2 + Math.random() * fullH;
      const r = 3 + Math.random() * 4;
      ctx.globalAlpha = 0.85 + Math.random() * 0.15;
      ctx.beginPath();
      ctx.moveTo(x - r * 1.6, y);
      ctx.lineTo(x, y - r * 0.28);
      ctx.lineTo(x + r * 1.6, y);
      ctx.lineTo(x, y + r * 0.28);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(x, y - r * 1.6);
      ctx.lineTo(x - r * 0.28, y);
      ctx.lineTo(x, y + r * 1.6);
      ctx.lineTo(x + r * 0.28, y);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x, y, r * 0.5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
    return;
  }

  // Black vinyl — solid black base. (Rare but real; some die-cut
  // labels use a black backing.)
  if (isBlackVinyl(material)) {
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(-fullW / 2, -fullH / 2, fullW, fullH);
    return;
  }

  // Default — plain white vinyl (most common).
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(-fullW / 2, -fullH / 2, fullW, fullH);
}

// ---------------------------------------------------------------------
// HELPERS
// ---------------------------------------------------------------------

/**
 * Read a Blob/File into an HTMLImageElement so we can draw it onto a
 * canvas. We use createObjectURL rather than a FileReader data-URL
 * round-trip because object URLs are O(1) (no base64 cost).
 *
 * iter114o — when the upload is a PDF (most professional graphics
 * clients deliver vector artwork that way), browsers can't decode it
 * via `<img>`. We lazy-load `pdfjs-dist` and rasterise the first page
 * to a canvas, then return that as an HTMLImageElement so the rest
 * of the compositor pipeline doesn't need a special code path.
 */
const blobToImage = async (blob) => {
  // Detect PDF via MIME type or .pdf extension. Lazy-import so the
  // ~150 KB pdfjs-dist bundle only ships when an actual PDF arrives.
  const { isPdfBlob, pdfBlobToImage } = await import('./pdfToImage');
  if (isPdfBlob(blob)) {
    return pdfBlobToImage(blob);
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(
        'Could not decode artwork as an image. ' +
        'Try a PNG, JPG, WebP, or PDF.',
      ));
    };
    img.src = url;
  });
};

/**
 * Apply the customer-selected cut shape as a canvas clip path. Caller
 * is responsible for save()/restore() book-keeping around this call.
 *
 * Centred on (0, 0); caller must `translate(cx, cy)` first.
 *
 * iter114s — `customContour` (a normalised array of points produced by
 * `traceArtworkContour`) is used to build the `'custom'` cut path so
 * the die-cut hugs the artwork's silhouette ("AI traces your artwork
 * outline" finally lives up to its promise). When the contour is null
 * (artwork is fully opaque, e.g. a PDF without `whiteInkOption='clear'`)
 * the cut falls back to a rectangle so the customer still sees the
 * full design.
 *
 * `radialExpand` (in canvas pixels) inflates the custom contour
 * outwards along each point's radial vector so the die-cut margin
 * (where the material backing shows around the artwork) follows the
 * SAME silhouette — instead of leaving a rectangle of holographic
 * around an irregularly-shaped character.
 */
const clipToCutShape = (ctx, shape, w, h, customContour = null, radialExpand = 0) => {
  ctx.beginPath();
  switch (shape) {
    case 'circle': {
      // iter114s.1 — use the LONGER artwork dimension so the circle
      // inscribes the entire bounding rectangle (no more "Spiderman's
      // arm got cut off"). Old: `min(w,h)/2` produced a circle that
      // clipped any non-square artwork. New: `max(w,h)/2 * 1.06`
      // gives a 6% breathing margin so the artwork sits comfortably
      // inside the circle with the material backing showing around it.
      const r = (Math.max(w, h) / 2) * 1.06;
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      break;
    }
    case 'rounded-rectangle': {
      const r = Math.min(w, h) * 0.12;
      const x = -w / 2;
      const y = -h / 2;
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r);
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      break;
    }
    case 'custom': {
      // iter114s.1 — render the contour as a SMOOTH curve via
      // quadratic Bézier midpoint interpolation instead of straight
      // `lineTo` polygon segments. For each contour point Pi the
      // curve uses Pi as the control and the midpoint(Pi, Pi+1) as
      // the endpoint, producing a continuous C1-smooth path that
      // looks like a real die-cut shape rather than a 180-sided
      // polygon. The customer-flagged "jagged die line" goes away
      // entirely with this single change.
      if (Array.isArray(customContour) && customContour.length >= 3) {
        const N = customContour.length;
        const halfW = w / 2;
        const halfH = h / 2;
        // Pre-scale & inflate every point ONCE so the per-segment
        // bezier loop below can stay tight. radialExpand is the
        // die-cut margin in canvas pixels.
        const scaled = new Array(N);
        for (let i = 0; i < N; i += 1) {
          let px = customContour[i].x * halfW;
          let py = customContour[i].y * halfH;
          if (radialExpand !== 0) {
            const len = Math.sqrt(px * px + py * py) || 1;
            px += (px / len) * radialExpand;
            py += (py / len) * radialExpand;
          }
          scaled[i] = { x: px, y: py };
        }
        // Start at the midpoint of the last→first segment so the
        // closed loop has no visible seam.
        const startMx = (scaled[N - 1].x + scaled[0].x) / 2;
        const startMy = (scaled[N - 1].y + scaled[0].y) / 2;
        ctx.moveTo(startMx, startMy);
        for (let i = 0; i < N; i += 1) {
          const next = scaled[(i + 1) % N];
          const mx = (scaled[i].x + next.x) / 2;
          const my = (scaled[i].y + next.y) / 2;
          ctx.quadraticCurveTo(scaled[i].x, scaled[i].y, mx, my);
        }
        break;
      }
      // Fallback for artworks where contour tracing failed (no opaque
      // pixels found — e.g., a fully-white PDF without white-key).
      ctx.rect(-w / 2, -h / 2, w, h);
      break;
    }
    case 'rectangle':
    default: {
      ctx.rect(-w / 2, -h / 2, w, h);
      break;
    }
  }
  ctx.closePath();
  ctx.clip();
};

/**
 * Apply the "Make white areas clear" pixel transform (iter114s).
 *
 * Converts near-white opaque pixels (R/G/B all ≥ 240) to fully
 * transparent so the material backing (holographic rainbow / glitter
 * speckle / clear plastic) shows through the natural background of
 * the design. Mirrors the backend's `_apply_make_white_clear` logic
 * (see `backend/utils/artwork_renderer.py`) so the live mockup,
 * proof PDF, and print file all agree visually.
 *
 * Critical for PDF uploads — pdfjs-dist rasterises pages with the
 * PDF spec's default opaque-white background, so without this pass
 * the holographic rainbow gets blocked by the rectangular white
 * canvas. After keying, only the actual artwork strokes / shapes
 * remain opaque and the customer's "Make white areas clear" pick is
 * finally honoured for vector uploads.
 *
 * Returns an HTMLCanvasElement (which `drawImage` happily accepts as
 * a CanvasImageSource).
 */
const applyWhiteKeyToArtwork = (img, threshold = 240) => {
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const off = document.createElement('canvas');
  off.width = w;
  off.height = h;
  const ctx = off.getContext('2d');
  if (!ctx) return img;
  ctx.drawImage(img, 0, 0);
  let imgData;
  try {
    imgData = ctx.getImageData(0, 0, w, h);
  } catch (err) {
    // Cross-origin tainted canvas — give up and return the original.
    return img;
  }
  const d = imgData.data;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i] >= threshold && d[i + 1] >= threshold && d[i + 2] >= threshold) {
      d[i + 3] = 0;
    }
  }
  ctx.putImageData(imgData, 0, 0);
  return off;
};

/**
 * Trace a smooth contour around the artwork's opaque silhouette.
 *
 * Approach — radial scan from the image centroid: for `samples` angles
 * around the image, cast a ray outward and find the last opaque pixel
 * (alpha ≥ `alphaThreshold`). The resulting polygon hugs the visible
 * design's outer edge with a smooth, low-frequency contour suitable
 * for a die-cut path. Compared to marching-squares, this gives a
 * single-loop convex-leaning hull (no internal holes, no detail
 * smaller than 360/samples degrees) which is exactly what a real
 * die-cut machine produces — and crucially, much faster (O(samples *
 * maxRadius) vs O(W*H)).
 *
 * Returns `null` when the artwork is empty or fully opaque (the
 * caller falls back to a rectangle clip in that case).
 *
 * Output points are normalised to roughly [-1, 1] relative to the
 * image's centre so the clip path can be scaled to any destination
 * size in `clipToCutShape`.
 */
const traceArtworkContour = (imgOrCanvas, samples = 96, alphaThreshold = 32) => {
  const w = imgOrCanvas.naturalWidth || imgOrCanvas.width;
  const h = imgOrCanvas.naturalHeight || imgOrCanvas.height;
  if (!w || !h) return null;
  const off = document.createElement('canvas');
  off.width = w;
  off.height = h;
  const ctx = off.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(imgOrCanvas, 0, 0);
  let imgData;
  try {
    imgData = ctx.getImageData(0, 0, w, h);
  } catch (err) {
    return null;
  }
  const d = imgData.data;
  const totalPixels = w * h;

  const isOpaque = (x, y) => {
    const ix = x | 0;
    const iy = y | 0;
    if (ix < 0 || ix >= w || iy < 0 || iy >= h) return false;
    return d[(iy * w + ix) * 4 + 3] >= alphaThreshold;
  };

  // Single full-pass scan: collect opaque count + centroid in one go.
  // For a 1600x1000 image this is 1.6M ops — well under 50 ms even in
  // the slowest browsers, and it guarantees we never under-sample tiny
  // test images.
  let opaqueCount = 0;
  let sumX = 0;
  let sumY = 0;
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      if (d[(y * w + x) * 4 + 3] >= alphaThreshold) {
        opaqueCount += 1;
        sumX += x;
        sumY += y;
      }
    }
  }
  if (opaqueCount === 0) return null;
  // ~fully opaque (>97%) → no useful silhouette to trace, fall back
  // to a rectangle in clipToCutShape.
  if (opaqueCount > totalPixels * 0.97) return null;

  const cx = sumX / opaqueCount;
  const cy = sumY / opaqueCount;

  const maxR = Math.max(
    Math.hypot(cx, cy),
    Math.hypot(w - cx, cy),
    Math.hypot(cx, h - cy),
    Math.hypot(w - cx, h - cy),
  );

  const radii = new Array(samples).fill(0);
  for (let i = 0; i < samples; i += 1) {
    const angle = (i / samples) * Math.PI * 2;
    const dx = Math.cos(angle);
    const dy = Math.sin(angle);
    let lastR = 0;
    // Step inward from the outer bound to find the outermost opaque
    // pixel along this ray. Pixel-step (1 px) is fine for our scan
    // since maxR is ≤ ~1500 for typical artwork.
    for (let r = maxR; r >= 1; r -= 1) {
      if (isOpaque(cx + dx * r, cy + dy * r)) {
        lastR = r;
        break;
      }
    }
    radii[i] = lastR;
  }

  // iter114s.17 — wider 11-point smoothing window + 7% inflation for a
  // visibly softer, less-toothy custom die-line. Customer feedback
  // 2026-05-08: "the die line always needs to be more simplified
  // (less end points), no sharp lines, always curved just like the
  // pdf proof renders them." Combined with the sample-count drop from
  // 240→96 above, the resulting silhouette has ~⅓ the control points
  // and a wider rolling average, so the quadratic-Bézier midpoint
  // pass in `clipToCutShape` produces a smooth, PDF-proof-style
  // contour instead of the previous 240-edge polygon.
  //
  //   1. Compute an 11-point mean for visual smoothness.
  //   2. Floor each smoothed value at the ORIGINAL detected radius —
  //      so a radial sample with a thin protrusion (Spider-Man's arm,
  //      a sticking-out limb) is never pulled in by neighbour
  //      averaging. Was the customer-flagged "artwork getting cut
  //      off" bug on iter114s.1 and we keep this guard.
  //   3. Apply a 7% radial inflation (was 5%) as a safety margin so
  //      anti-aliased edge pixels (which are below `alphaThreshold`
  //      and therefore not detected as opaque) still fall inside the
  //      contour, with extra slack for the wider smoothing window.
  const meanSmoothed = radii.map((_, i) => {
    let sum = 0;
    let n = 0;
    for (let k = -5; k <= 5; k += 1) {
      const v = radii[(i + k + samples) % samples];
      if (v > 0) {
        sum += v;
        n += 1;
      }
    }
    return n > 0 ? sum / n : 0;
  });
  const INFLATION = 1.07;
  const smoothed = radii.map((rad, i) => Math.max(meanSmoothed[i], rad) * INFLATION);

  // Normalise relative to the image centre so the contour scales
  // cleanly to any destination size in clipToCutShape.
  const halfW = w / 2;
  const halfH = h / 2;
  // We use the image centre (not the centroid) for normalisation so
  // the polygon stays centred on the destination canvas regardless
  // of where the artwork's centre of mass landed.
  const points = [];
  for (let i = 0; i < samples; i += 1) {
    if (smoothed[i] <= 0) continue;
    const angle = (i / samples) * Math.PI * 2;
    const px = cx + Math.cos(angle) * smoothed[i];
    const py = cy + Math.sin(angle) * smoothed[i];
    // Translate so image centre is the origin, then normalise to
    // [-1, 1] using the image's half-extents.
    points.push({
      x: (px - halfW) / halfW,
      y: (py - halfH) / halfH,
    });
  }
  if (points.length < 3) return null;
  return points;
};

/**
 * Paint a single sticker instance: drop shadow + material-aware backing
 * + clipped artwork.
 *
 * iter114s — `customContour` is the alpha-traced silhouette polygon
 * shared across all 5 sticker copies (computed once per render).
 * Forwarded into `clipToCutShape` only when `cutShape === 'custom'`,
 * so the rectangle / rounded / circle paths are untouched.
 */
const drawSticker = (ctx, artworkImg, cutShape, material, customContour, textures, { cx, cy, size, rot }) => {
  const ratio = artworkImg.width / artworkImg.height || 1;
  // iter114s.2 — for the Circle cut, scale the artwork to ~78% of the
  // slot diameter so it inscribes comfortably with a visible margin
  // around it (matches the customer's "Circle Die-line.pdf" gold
  // standard where the character has clear whitespace around it
  // inside the round cut). Other cut shapes leave the artwork at the
  // full slot dimensions so rectangle / rounded / custom still touch
  // the slot edges as expected.
  const artworkFitFactor = cutShape === 'circle' ? 0.78 : 1.0;
  const targetSize = size * artworkFitFactor;
  let w;
  let h;
  if (ratio >= 1) {
    w = targetSize;
    h = targetSize / ratio;
  } else {
    w = targetSize * ratio;
    h = targetSize;
  }
  // Die-cut margin ~6% of the longer edge — matches the 1/8" white
  // border printed around custom stickers (and looks natural on
  // holographic / glitter / foil too).
  const border = size * 0.06;

  // Slot dimensions — the cut path geometry. Circle FORCES a square
  // slot so the resulting circle is geometrically perfect regardless
  // of artwork aspect (no more "the circle looks tight on tall
  // artwork"). CUSTOM uses the artwork rect itself (no border padding)
  // because the die-cut margin is achieved via radial INFLATION of
  // the contour — adding `border * 2` would scale the contour
  // dimensions beyond the artwork, leaving the character visibly
  // off-centre inside an over-padded silhouette (iter114s.3 customer
  // bug). Other cut shapes follow the artwork rect + die-cut border.
  const slotW = cutShape === 'circle' ? size
              : cutShape === 'custom' ? w
              : (w + border * 2);
  const slotH = cutShape === 'circle' ? size
              : cutShape === 'custom' ? h
              : (h + border * 2);

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((rot * Math.PI) / 180);

  // 1. Drop shadow — darkened, blurred stamp behind the sticker.
  ctx.save();
  ctx.translate(8, 14);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
  ctx.filter = 'blur(14px)';
  clipToCutShape(ctx, cutShape, slotW, slotH, customContour, border);
  ctx.fillRect(-slotW, -slotH, slotW * 2, slotH * 2);
  ctx.restore();

  // 2. Material-aware backing — fills the FULL slot so glitter /
  //    holographic / foil shows around the artwork even when the
  //    artwork is shrunk inside a circle slot.
  ctx.save();
  clipToCutShape(ctx, cutShape, slotW, slotH, customContour, border);
  paintMaterialBacking(ctx, material, slotW + border * 2, slotH + border * 2, textures);
  ctx.restore();

  // 3. Artwork layer — clipped to the SAME slot path (NOT a tight
  //    artwork-rect clip) so a circle slot never trims an inscribed
  //    artwork. The artwork image is drawn at its scaled (w, h) and
  //    centred; the rest of the slot shows the material backing.
  ctx.save();
  clipToCutShape(ctx, cutShape, slotW, slotH, customContour, 0);
  ctx.drawImage(artworkImg, -w / 2, -h / 2, w, h);
  ctx.restore();

  ctx.restore();
};

/**
 * Paint a single subtle "PREVIEW" stamp in the bottom-right corner.
 * iter114m — replaces the iter114l diagonal-repeating watermark
 * (which the customer felt dominated the image and read as "preview-
 * eg3 background"). The new treatment is one small pill in a corner
 * just enough to flag this as a preview render for IP protection,
 * without competing with the customer's artwork.
 *
 * Watermark colour adapts to the scene (light text on dark surfaces,
 * dark text on light surfaces) so the stamp is always legible but
 * never overpowering.
 */
const drawWatermark = (ctx, color) => {
  ctx.save();
  ctx.font = '700 26px system-ui, sans-serif';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = color || 'rgba(255, 255, 255, 0.30)';
  ctx.fillText('PREVIEW', CANVAS_W - 36, CANVAS_H - 36);
  ctx.restore();
};

// ---------------------------------------------------------------------
// PUBLIC ENTRY
// ---------------------------------------------------------------------

/**
 * Render the customer's artwork as a sticker-pack mockup with a
 * material-aware backing and a deterministically-rotated background
 * scene. Returns a PNG data URL.
 *
 * @param {Object}     args
 * @param {Blob|File}  args.artworkBlob   — required.
 * @param {string}     [args.cutShape]    — rectangle | rounded-rectangle | circle | custom.
 * @param {string}     [args.material]    — material name (substring-matched for holo/glitter/clear/foil).
 * @param {string}     [args.sceneSeed]   — string used to pick scene; same seed → same scene.
 * @param {boolean}    [args.watermark]   — default true.
 */
/**
 * Render the customer's artwork as a sticker-pack mockup with a
 * material-aware backing and a deterministically-rotated background
 * scene. Returns a PNG data URL.
 *
 * @param {Object}     args
 * @param {Blob|File}  args.artworkBlob       — required.
 * @param {string}     [args.cutShape]        — rectangle | rounded-rectangle | circle | custom.
 * @param {string}     [args.material]        — material name (substring-matched for holo/glitter/clear/foil).
 * @param {string}     [args.sceneSeed]       — string used to pick scene; same seed → same scene.
 * @param {boolean}    [args.watermark]       — default true.
 * @param {string}     [args.whiteInkOption]  — 'keep' (default) | 'clear'. When 'clear',
 *   near-white opaque pixels in the artwork are stripped to transparent so the material
 *   backing (holographic rainbow, glitter speckle, …) shows through the design's natural
 *   negative space — critical for PDF uploads which rasterise with an opaque-white
 *   background by default.
 */
export const renderCanvasMockup = async ({
  artworkBlob,
  cutShape = 'rectangle',
  material = '',
  sceneSeed = 'default',
  watermark = true,
  whiteInkOption = 'keep',
  customContourOverride = null,
}) => {
  if (!artworkBlob) throw new Error('Artwork is required for the live mockup.');

  const decodedArtwork = await blobToImage(artworkBlob);

  // White-ink-clear pass — promote near-white opaque pixels to fully
  // transparent so the customer-selected option is honoured visually,
  // regardless of whether the upload was a PNG (already-keyed alpha)
  // or a PDF (pdfjs rasterises with opaque-white default page bg).
  const artworkImg = whiteInkOption === 'clear'
    ? applyWhiteKeyToArtwork(decodedArtwork)
    : decodedArtwork;

  // Custom-cut contour — iter114s.29 PREFERS the high-quality contour
  // computed by the backend (`POST /api/files/artwork/staging/{id}/contour`,
  // same OpenCV pipeline as the PDF proof) over the in-browser radial
  // scan. The radial scan stays as a fallback for guests/local-file
  // mockups where the artwork hasn't been staged on the backend yet.
  let customContour = null;
  if (cutShape === 'custom') {
    if (Array.isArray(customContourOverride) && customContourOverride.length >= 3) {
      customContour = customContourOverride;
    } else {
      customContour = traceArtworkContour(artworkImg);
    }
  }

  // iter114s.3 — preload material-specific textures (real glitter /
  // holographic JPG that the BACKEND uses for proof PDFs) so the live
  // mockup renders pixel-identical to the proof PDF. Loads happen in
  // parallel; null result = texture cache miss → procedural fallback.
  const texPromises = [];
  if (isGlitter(material))     texPromises.push(loadMaterialTexture('glitter').then((t) => ['glitter', t]));
  if (isHolographic(material)) texPromises.push(loadMaterialTexture('holographic').then((t) => ['holographic', t]));
  const texEntries = await Promise.all(texPromises);
  const textures = Object.fromEntries(texEntries.filter(([, v]) => v));

  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_W;
  canvas.height = CANVAS_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is unavailable in this browser.');

  const scene = pickScene(sceneSeed);
  scene.paint(ctx);

  STICKER_LAYOUT.forEach((entry) => {
    drawSticker(ctx, artworkImg, cutShape, material, customContour, textures, entry);
  });

  if (watermark) drawWatermark(ctx, scene.watermarkColor);

  return canvas.toDataURL('image/png');
};

/**
 * Test hook — exported for unit tests so we can assert the scene-
 * picker is deterministic without driving the full canvas render.
 */
export const _internal = {
  hashToIndex,
  pickScene,
  SCENES,
  applyWhiteKeyToArtwork,
  traceArtworkContour,
  clipToCutShape,
};
